<template>
    <div
        ref="rootMenu"
        class="mobile-nav"
        :inert="$bp.bps.isSmallDesktop"
    >
        <div class="controls">
            <transition name="back">
                <button v-if="selected[0]" class="btn back" @click="selected = []">
                    <DSFontIcon code="arrow-left" icon-style="line" size="l" />
                </button>
            </transition>
            <button class="btn close" @click="layout.sidebars.right = false">
                <DSFontIcon
                    code="close"
                    icon-set="st1website"
                    icon-style="line"
                    size="xxl"
                />
            </button>
        </div>
        <transition-group
            tag="div"
            name="panels"
            class="panels"
            :class="{
                [panelTransitionClass]: true,
            }"
            @after-leave="panelLeft"
            @before-leave="panelLeave"
        >
            <div v-if="!selected.length" key="panel-level-0" class="panel panel-0">
                <ul class="root-level">
                    <li
                        v-for="item, key in links"
                        :key="`link-${item.id}-${key}`"
                    >
                        <nuxt-link
                            :class="{
                                'selected': route.path.includes(`/${item.url}`),
                            }"
                            :to="`/${item.url}`"
                            @click="closeNav"
                        >
                            {{ item.text }}
                        </nuxt-link>
                    </li>
                </ul>
                <LayoutUserMenu v-if="userMenu" class="theme-white">
                    <template #user-icon><i></i></template>
                </LayoutUserMenu>
                <LayoutSearch />
                <ul class="nav level-0">
                    <li
                        v-for="item, key in navigationOne"
                        :key="`item-${item.id}-${key}`"
                    >
                        <nuxt-link :to="`/${item.url}`" @click="closeNav">{{ item.text }}</nuxt-link>
                        <button v-if="item.children.length" @click="selected = [item.id]">
                            <DSFontIcon
                                code="arrow-right"
                                icon-style="filled"
                                icon-set="st1website"
                                size="l"
                            />
                        </button>
                    </li>
                </ul>
                <div v-if="$paths.getPath('stationLocator') !== '/'" class="additional-links">
                    <nuxt-link :to="$paths.getPath('contact')" @click="closeNav">
                        <span class="bg"><DSFontIcon code="message" icon-style="line" size="l" /></span>
                        <span>{{ $i18n.t('links.contact') }}</span>
                    </nuxt-link>
                    <nuxt-link :to="$paths.getPath('stationLocator')" @click="closeNav">
                        <span class="bg"><DSFontIcon code="location" icon-style="line" size="l" /></span>
                        <span>{{ $i18n.t('links.stationLocator') }}</span>
                    </nuxt-link>
                </div>
            </div>
            <div v-if="subPanel" key="panel-level-1" class="panel sub">
                <div class="illustration">
                    <DSFrontifyAsset
                        v-if="subPanel.navigationImage"
                        class="sub-nav-illustration"
                        :asset="subPanel.navigationImage"
                    />
                    <h3 class="h2">{{ subPanel.text }}</h3>
                </div>
                <ul class="nav level-1">
                    <li
                        v-for="item, key in subPanel.children"
                        :key="`sub-item-${item.id}-${key}`"
                    >
                        <nuxt-link :to="`/${item.url}`" @click="closeNav">{{ item.text }}</nuxt-link>
                    </li>
                </ul>
                <div v-if="$paths.getPath('stationLocator') !== '/'" class="additional-links">
                    <nuxt-link :to="$paths.getPath('stationLocator')" @click="closeNav">
                        <span class="bg"><DSFontIcon code="location" icon-style="line" size="l" /></span>
                        <span>{{ $i18n.t('links.stationLocator') }}</span>
                    </nuxt-link>
                </div>
            </div>
        </transition-group>
    </div>
</template>
<script setup>
import { ref, computed, provide, watch, inject, useRoute, useNuxtApp, useI18n } from '#imports';
import { useSwipe } from '@vueuse/core';
const { $i18n, $paths, $bp } = useNuxtApp();
const route = useRoute();
const selected = ref([]);
const layout = inject('layout');
const panelTransitionClass = ref('');
const rootMenu = ref(null);

useSwipe(rootMenu, {
    onSwipeEnd: (event, direction) => {
        if (direction === 'right') {
            if (selected.value.length === 1) {
                selected.value = [];
            } else {
                closeNav();
            }
        }
    },
});

provide('selected', selected);

const props = defineProps({
    links: {
        type: Array,
        required: false,
        default: () => [],
    },
    userMenu: {
        type: Boolean,
        required: false,
        default: true,
    },
});

const routeArray = computed(() => {
    const routeArray = route.path.split('/');

    return routeArray.filter(value => value !== '' && value !== 'preview');
});

const navigationOne = computed(() => {
    if (!props.links?.map(item => item.url).includes(routeArray.value[0])) {
        return props.links?.[0]?.children ?? [];
    }

    return props.links.find(item => item.url === routeArray.value[0])?.children;
});
const subPanel = computed(() => navigationOne.value?.find(item => item.id === selected.value[0]));

watch(() => selected.value, (to, from) => {
    if (to.length > from.length) {
        panelTransitionClass.value = 'leave-to-left';
    } else {
        panelTransitionClass.value = 'enter-from-left';
    }
});

const panelLeave = el => {
    const { width } = el.getBoundingClientRect();
    Object.assign(el.style, {
        width: `${width}px`,
    });
};

const panelLeft = () => {
    panelTransitionClass.value = '';
};

const closeNav = () => {
    layout.value.sidebars = {
        left: false,
        right: false,
    };
};

useI18n({
    useScope: 'global',
    messages: {
        'en-EU': {
            links: {
                contact: 'Contact us',
                stationLocator: 'Nearest station',
            },
        },
        'fi-FI': {
            links: {
                contact: 'Ota yhteyttä',
                stationLocator: 'Lähin asema',
            },
        },
        'sv-SE': {
            links: {
                contact: 'Kontakta oss',
                stationLocator: 'Närmaste station',
            },
        },
        'no-NO': {
            links: {
                contact: 'Kontakt oss',
                stationLocator: 'Nærmeste stasjon',
            },
        },
    },
});
</script>
<style scoped>
button {
  -webkit-tap-highlight-color: transparent;
}

.mobile-nav {
  overflow-x: hidden;
  display: grid;
  grid-template-rows: repeat(3, auto) 1fr auto;
  min-height: 100%;
}

.illustration {
  display: flex;
  flex-flow: column nowrap;
  border-top: 2px solid var(--color-base-grey-300);
  padding-top: 1.125rem;
}
.illustration .image-container {
  width: 100%;
  padding-top: 36.5%;
  border-radius: 1rem;
}

.back-enter-active, .back-leave-active {
  transition: transform 0.5s cubic-bezier(0.19, 0.73, 0.23, 1);
}

.back-enter-from {
  opacity: 0;
  transform: translateY(1rem);
}

.back-leave-to {
  opacity: 0;
  transform: translateY(1rem);
}

.panels {
  padding: 0 1.5rem;
}
.panels.leave-to-left .panels-leave-to, .panels.enter-from-left .panels-enter-from {
  transform: translateX(calc(-100% - 1.5rem));
}

.panels-enter-active, .panels-leave-active {
  transition: transform 0.5s cubic-bezier(0.19, 0.73, 0.23, 1);
}

.panels-enter-from {
  transform: translateX(calc(100% + 1.5rem));
}

.panels-leave-to {
  position: absolute;
  transform: translateX(calc(100% + 1.5rem));
}

.nav {
  list-style: none;
  margin: 0;
  padding: 0;
}

.panel {
  display: flex;
  flex-flow: row wrap;
}
.panel.sub .h2 {
  font-weight: 600;
}
.panel > *, .panel > ul {
  width: 100%;
  padding-bottom: 1.125rem;
  border-bottom: none;
}
.panel > *:not(:nth-child(3)), .panel > ul:not(:nth-child(3)) {
  border-bottom: 2px solid var(--color-base-grey-300);
  margin-bottom: 1.125rem;
}

.level-0 li {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
}
.level-0 a {
  display: inline-flex;
  font-size: 1.75rem;
  line-height: 1;
  padding: 1.375rem 1.5rem 1.375rem 0;
  font-weight: 600;
}
.level-0 .icon {
  transition: all 0.75s cubic-bezier(0.77, 1.34, 0.63, 1);
  opacity: 1;
  color: var(--color-base-grey-500);
}
.level-0.selected a {
  padding-top: 0;
}
.level-0.selected .icon {
  pointer-events: none;
  opacity: 0;
}

.level-1 li:not(:last-child) {
  margin-bottom: 1rem;
}
.level-1 a {
  display: inline-flex;
  font-size: 1.375rem;
  line-height: 1;
}

.additional-links a {
  display: flex;
  font-size: 1.125rem;
  align-items: center;
}
.additional-links a .bg {
  width: 4rem;
  height: 4rem;
  background: var(--color-base-grey-200);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-right: 0.875rem;
}
.additional-links a:not(:last-child) {
  margin-bottom: 0.5rem;
}

.controls {
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding: 1.5rem 1.5rem 0;
}
.controls .btn {
  display: inline-flex;
  align-items: center;
}
.controls .btn.close {
  margin-left: auto;
}

.root-level {
  display: flex;
  flex-flow: row wrap;
  margin: 0;
  padding: 0;
  list-style: none;
}
.root-level li {
  display: inline-flex;
}
.root-level li:not(:last-child) {
  margin-right: 0.5rem;
}
.root-level a {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  border-width: 1px;
  border-style: solid;
  border-color: transparent;
  border-radius: 1.25rem;
  letter-spacing: 0.025em;
}
.root-level a.selected {
  border-color: var(--color-secondary-50);
  background: var(--color-secondary-50);
}

.user {
  width: 100%;
}
</style>
